import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { useFlash } from "@/floor/useFlash";
import { getRecent, pushRecent, useFloorActions } from "@/lib/floor";
import { useGoDesk, useFloorReturn } from "@/lib/floorReturn";

import { Truck, Package, Boxes, ScanLine, PackagePlus, Camera, MapPin, PackageMinus, ArrowRight, AlertTriangle, Printer } from "lucide-react";

const KIND_ROUTE = {
  part: (id) => `/floor/part/${id}`,
  donor: (id) => `/floor/donor/${id}`,
  location: (id) => `/floor/location/${id}`,
};
const KIND_META = {
  part: { icon: Package, label: "Part" },
  donor: { icon: Truck, label: "Truck" },
  location: { icon: Boxes, label: "Location" },
};

// Big touch tile — mirrors the desk Home "quick hits" pattern (HomePage QuickHit): the
// SCAN tile is the prominent red primary (spans 2), everything else is a clean white target.
function FloorTile({ label, icon: Icon, tone = "white", onClick, testid, badge }) {
  const scan = tone === "scan";
  const alert = tone === "alert";
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`group text-left rounded-2xl border p-6 min-h-[132px] flex flex-col justify-between active:scale-[0.98] transition-transform duration-100 ${
        scan ? "col-span-2 border-[#B01E28] bg-[#B01E28] shadow-md"
        : alert ? "border-amber-300 bg-amber-50 shadow-sm hover:border-amber-400"
        : "border-slate-200 bg-white shadow-sm hover:border-[#E0781A]"}`}>
      <div className="flex items-center justify-between">
        <div className={`rounded-xl flex items-center justify-center ${scan ? "w-16 h-16 bg-white/15" : alert ? "w-14 h-14 bg-amber-200" : "w-14 h-14 bg-red-50"}`}>
          <Icon className={scan ? "w-9 h-9 text-white" : alert ? "w-8 h-8 text-amber-700" : "w-8 h-8 text-[#B01E28]"} strokeWidth={2.2} />
        </div>
        {badge != null
          ? <span className="rounded-full bg-amber-600 text-white text-lg font-black min-w-[2rem] h-8 px-2 flex items-center justify-center" data-testid={`${testid}-badge`}>{badge}</span>
          : <ArrowRight className={`w-6 h-6 ${scan ? "text-white/80" : "text-slate-200 group-hover:text-[#B01E28]"}`} />}
      </div>
      <div className={`mt-3 font-display font-black leading-tight ${scan ? "text-white text-3xl" : alert ? "text-amber-900 text-xl" : "text-[#1F1F1F] text-xl"}`}>{label}</div>
    </button>
  );
}

export default function FloorHome() {
  const navigate = useNavigate();
  const { has } = useAuth();
  const goDesk = useGoDesk();
  const fr = useFloorReturn();
  const [scanOpen, setScanOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState(getRecent());
  const [problemCount, setProblemCount] = useState(0);
  const [recoverCount, setRecoverCount] = useState(0);
  const [putawayReturns, setPutawayReturns] = useState(0);
  const [putawayScope, setPutawayScope] = useState(null);
  const [stuckCount, setStuckCount] = useState(0);
  const [stuckHours, setStuckHours] = useState(48);
  const [stuckReturning, setStuckReturning] = useState(0);
  const [shelfCount, setShelfCount] = useState(0);
  const { trigger, FlashOverlay } = useFlash();
  const homeActions = useFloorActions("home");   // enabled-only, owner-sorted
  const hasScanTile = homeActions.some((a) => a.code === "home_scan");

  // Reaching Floor Home always ends any round-trip into a desk screen.
  useEffect(() => { fr?.setActive(false); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!has("view_problem_moves")) return;
    let alive = true;
    const load = () => api.get("/problem-moves/count").then(({ data }) => alive && setProblemCount(data.unresolved_count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  // Print runs that dropped mid-batch — a tech must see this on the FLOOR, not only Desk.
  useEffect(() => {
    if (!has("generate_print_labels")) return;
    let alive = true;
    const load = () => api.get("/tags/recovery").then(({ data }) => alive && setRecoverCount(data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  // Parts returned/received with no bin yet. ONLY a RETURN raises an alert here: somebody is
  // holding it and it piles up unseen. A sellable part with no bin recorded is a data gap, not
  // a job, and it lives as one line on the put-away screen (his split, 2026-10-08).
  useEffect(() => {
    if (!has("putaway")) return;
    let alive = true;
    const load = () => api.get("/putaway/queue/count").then(({ data }) => {
      if (!alive) return;
      setPutawayReturns(data.returns_waiting ?? data.returned_count ?? 0);
      setPutawayScope(data.scope || null);
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  // Containers on no shelf with parts inside — the crew's clean-up checklist (his call, 2026-10-21).
  useEffect(() => {
    if (!has("putaway")) return;
    api.get("/shelf-cleanup/summary").then(({ data }) =>
      setShelfCount((data.now?.containers_with_parts || 0) + (data.now?.lot_crates_with_parts || 0))).catch(() => {});
  }, [has]);

  // Loads sent but never received past the owner threshold — the in-transit limbo (normal OR rejected).
  useEffect(() => {
    if (!has("receive_transfer")) return;
    let alive = true;
    const load = () => api.get("/transfers/stuck/count").then(({ data }) => {
      if (!alive) return;
      setStuckCount(data.total || 0); setStuckHours(data.threshold_hours || 48); setStuckReturning(data.returning_count || 0);
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  const onScan = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get("/scan/resolve", { params: { code } });
      trigger("success", (KIND_META[data.kind]?.label || "Found").toUpperCase());
      setRecent(pushRecent(data));
      setScanOpen(false);
      // scan-to-fix: a tote with an open problem move jumps straight to the resolver.
      if (data.open_problem_move_id) {
        setTimeout(() => navigate(`/floor/problem/${data.open_problem_move_id}`), 240);
        return;
      }
      const route = KIND_ROUTE[data.kind];
      setTimeout(() => navigate(route ? route(data.id) : "/floor"), 240);
    } catch {
      trigger("error", "NOT FOUND");
    } finally { setBusy(false); }
  };

  // Floor Home tiles are owner-editable (label/order/show-hide) via floor_actions 'home'.
  // Each code maps to its fixed capability + route here (config can't invent routes/perms).
  const HOME_TILE_DEFS = {
    home_scan:    { icon: ScanLine,     tone: "scan",  onClick: () => setScanOpen(true) },
    home_tag:     { icon: PackagePlus,  perm: "manage_parts", onClick: () => goDesk("/capture") },
    home_rapid:   { icon: Camera,       perm: "manage_parts", onClick: () => goDesk("/rapid-capture") },
    home_putaway: { icon: MapPin,       perm: "putaway",      onClick: () => navigate("/floor/putaway") },
    home_pick:    { icon: PackageMinus, perm: "pick_remove",  onClick: () => goDesk("/pick") },
  };

  return (
    <div className="flex-1 flex flex-col gap-6 p-4 sm:p-6" data-testid="floor-home">
      {FlashOverlay}

      {/* Quick-hits grid — owner-configured tiles (Scan always present), then STATE-DRIVEN
          alert tiles (Problem Moves / Print recovery) which surface on their own and are
          intentionally NOT part of the editor. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {homeActions.map((a) => {
          const def = HOME_TILE_DEFS[a.code];
          if (!def) return null;
          if (def.perm && !has(def.perm)) return null;
          return <FloorTile key={a.code} testid={`floor-tile-${a.code.replace("home_", "")}`}
            label={a.label} icon={def.icon} tone={def.tone || "white"} onClick={def.onClick} />;
        })}
        {/* Safety net: the Scan entry point must never vanish, even if the config fetch fails. */}
        {!hasScanTile && (
          <FloorTile testid="floor-tile-scan" label="Scan / Look Up" icon={ScanLine} tone="scan" onClick={() => setScanOpen(true)} />
        )}
        {has("view_problem_moves") && problemCount > 0 && (
          <FloorTile testid="floor-tile-problems" label="Problem Moves" icon={AlertTriangle} tone="alert" badge={problemCount} onClick={() => navigate("/floor/problems")} />
        )}
        {has("generate_print_labels") && recoverCount > 0 && (
          <FloorTile testid="floor-tile-print-recovery" label="Print runs to recover" icon={Printer} tone="alert" badge={recoverCount} onClick={() => goDesk("/make-tags")} />
        )}
        {has("putaway") && putawayReturns > 0 && (
          <FloorTile testid="floor-tile-putaway-alert"
            label={`Returns waiting for a bin · ${putawayScope?.site_scoped ? putawayScope.site_name : "all sites"}`}
            icon={MapPin} tone="alert" badge={putawayReturns} onClick={() => navigate("/floor/putaway")} />
        )}
        {has("receive_transfer") && stuckCount > 0 && (
          <FloorTile testid="floor-tile-stuck-transfers"
            label={`Not received in ${stuckHours}h${stuckReturning > 0 ? ` (${stuckReturning} rejected)` : ""}`}
            icon={Truck} tone="alert" badge={stuckCount} onClick={() => goDesk("/transfers?stuck=1")} />
        )}
        {has("putaway") && shelfCount > 0 && (
          <FloorTile testid="floor-tile-shelf-cleanup" label="Shelf clean-up" icon={MapPin} tone="alert"
            badge={shelfCount} onClick={() => navigate("/floor/shelve")} />
        )}
      </div>

      {/* Recent — fast back-and-forth to the last few scans (no camera; pure links) */}
      {recent.length > 0 && (
        <div data-testid="floor-recent">
          <div className="text-base font-bold uppercase tracking-[0.15em] text-slate-400 mb-2 px-1">Recent</div>
          <div className="flex gap-3 overflow-x-auto snap-x pb-1">
            {recent.map((r) => {
              const M = KIND_META[r.kind] || KIND_META.part;
              const route = KIND_ROUTE[r.kind];
              return (
                <button key={`${r.kind}-${r.id}`} data-testid={`floor-recent-${r.id}`}
                  onClick={() => route && navigate(route(r.id))}
                  className="snap-start shrink-0 w-44 h-28 rounded-xl bg-white border-2 border-[#E2E8F0] p-3 flex flex-col justify-between text-left active:scale-95 active:bg-slate-50 transition-[transform,background-color] duration-100">
                  <div className="flex items-center gap-2 text-slate-500">
                    <M.icon className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">{M.label}</span>
                  </div>
                  <div className="font-mono font-bold text-lg text-slate-900 truncate">{r.label || "—"}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Camera fires ON-DEMAND — only while this overlay is open; closed = camera released */}
      <FloorScanOverlay open={scanOpen} testid="floor-scan-overlay"
        title="Scan any tag" sub="Part · Truck · Bin · Tote" submitLabel="Look up"
        onScan={onScan} onClose={() => setScanOpen(false)} busy={busy} />
    </div>
  );
}
